import { InfoOutlined, Refresh } from "@mui/icons-material";
import { CircularProgress, IconButton } from "@mui/material";
import { startCase } from "lodash";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import {
  useCrudEditContext,
  useRideProductionsByIdsNavigation,
  useRidesByIdsNavigation,
} from "@ryde/redux";
import {
  DATA_E2E_KEY,
  editFormDataAF,
  EditFormMode,
  isISODateStr,
  IStationRouteDto,
  IStationRouteUsageStatsDto,
  Log,
  onActivateKeyDown,
  StationRouteApi,
  TOOLTIP_DELAY_BUTTONS,
  useFormats,
  useHaveCustomPermissions,
} from "@ryde/shared";

import {
  StationRouteStatisticsInfoStyled,
  StationRouteStatisticsLineStyled,
  StationRouteStatisticsPlateStyled,
  StationRouteStatisticsTabStyled,
} from "./station-route.styled";

const EMPTY_DATES_PLACEHOLDER = "-";

const emptyStats: IStationRouteUsageStatsDto = {
  rideIds: [],
  productionIds: [],
  ridesCreatedFrom: null,
  ridesCreatedTo: null,
};

export const StationRouteTabsTabStatistics: FC = () => {
  const { entity, mode } = useCrudEditContext<IStationRouteDto>();
  // editFormDataAF is untyped (any); annotate local value instead of casting.
  const stationRouteDto: IStationRouteDto | undefined = useRecoilValue(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const { t } = useTranslation();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const { hasPermissionViewRide, readProductionPermission } =
    useHaveCustomPermissions();
  const { openRidesByIdsInNewTab } = useRidesByIdsNavigation();
  const { openRideProductionsByIdsInNewTab } =
    useRideProductionsByIdsNavigation();

  const [stats, setStats] = useState<IStationRouteUsageStatsDto>(emptyStats);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const stationRouteId = stationRouteDto?.id;

  // Runs on mount (tab open) and Refresh; parent mounts this tab only when selected.
  const fetchStats = useCallback(async (): Promise<void> => {
    if (!stationRouteId) {
      setStats(emptyStats);
      setHasFetched(true);
      return;
    }
    setIsLoading(true);
    try {
      const result = await StationRouteApi.getUsageStats(stationRouteId);
      setStats(result ?? emptyStats);
    } catch (error) {
      Log.warn("Failed to load station route usage stats", error);
      setStats(emptyStats);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [stationRouteId]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  const handleRefresh = useEvent((): void => {
    void fetchStats();
  });

  const rideCount = stats.rideIds.length;
  const productionCount = stats.productionIds.length;
  // API returns full payload; UI filters by entity view permission.
  const showRides = !!hasPermissionViewRide;
  const showProductions = !!readProductionPermission;

  const ridesCreatedRangeLabel = useMemo(() => {
    const fromIso =
      stats.ridesCreatedFrom && isISODateStr(stats.ridesCreatedFrom)
        ? stats.ridesCreatedFrom
        : undefined;
    const toIso =
      stats.ridesCreatedTo && isISODateStr(stats.ridesCreatedTo)
        ? stats.ridesCreatedTo
        : undefined;
    if (!fromIso && !toIso) {
      return undefined;
    }
    const from = formatDateTimeWithSecondsNormalized(fromIso);
    const to = formatDateTimeWithSecondsNormalized(toIso);
    return `${from} - ${to}`;
  }, [
    formatDateTimeWithSecondsNormalized,
    stats.ridesCreatedFrom,
    stats.ridesCreatedTo,
  ]);

  if (!showRides && !showProductions) {
    return (
      <StationRouteStatisticsTabStyled>
        <p>{t("stationRoute.statistics.noPermission")}</p>
      </StationRouteStatisticsTabStyled>
    );
  }

  return (
    <StationRouteStatisticsTabStyled>
      <StationRouteStatisticsInfoStyled>
        <span className="statistics-info-icon" aria-hidden>
          <InfoOutlined fontSize="small" />
        </span>
        <span className="statistics-info-text">
          {t("stationRoute.statistics.usageInfo")}
        </span>
        <span className="statistics-info-refresh">
          <FramerTooltip
            content={t("general.REFRESH")}
            mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
          >
            <IconButton
              type="button"
              size="small"
              color="info"
              onClick={handleRefresh}
              disabled={isLoading || !stationRouteId}
              aria-label={t("general.REFRESH")}
              {...{ [DATA_E2E_KEY]: "stationRoute-statistics-refresh" }}
            >
              {isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Refresh fontSize="small" />
              )}
            </IconButton>
          </FramerTooltip>
        </span>
      </StationRouteStatisticsInfoStyled>

      {!hasFetched && isLoading ? (
        <p>{t("general.loading")}</p>
      ) : (
        <StationRouteStatisticsPlateStyled>
          {showRides && (
            <StationRouteStatisticsLineStyled $labelTone="rides">
              <label>{startCase(t("ride.plural"))}:</label>
              {rideCount > 0 ? (
                <span
                  className="link_clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => openRidesByIdsInNewTab(stats.rideIds)}
                  onKeyDown={onActivateKeyDown(() =>
                    openRidesByIdsInNewTab(stats.rideIds),
                  )}
                >
                  {rideCount}
                </span>
              ) : (
                <span>{rideCount}</span>
              )}
            </StationRouteStatisticsLineStyled>
          )}
          {showRides && (
            <StationRouteStatisticsLineStyled $labelTone="ridesCreated">
              <label>{t("stationRoute.statistics.ridesCreated")}:</label>
              <span>{ridesCreatedRangeLabel ?? EMPTY_DATES_PLACEHOLDER}</span>
            </StationRouteStatisticsLineStyled>
          )}
          {showProductions && (
            <StationRouteStatisticsLineStyled $labelTone="productions">
              <label>{startCase(t("rideProduction.plural"))}:</label>
              {productionCount > 0 ? (
                <span
                  className="link_clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    openRideProductionsByIdsInNewTab(stats.productionIds)
                  }
                  onKeyDown={onActivateKeyDown(() =>
                    openRideProductionsByIdsInNewTab(stats.productionIds),
                  )}
                >
                  {productionCount}
                </span>
              ) : (
                <span>{productionCount}</span>
              )}
            </StationRouteStatisticsLineStyled>
          )}
        </StationRouteStatisticsPlateStyled>
      )}
    </StationRouteStatisticsTabStyled>
  );
};
